/**
 * Retorno SENSORIAL do bipe (PRD §8.2): "beep de sucesso + vibração + flash
 * verde; falha = tela VERMELHA + beep grave".
 *
 * O flash e a tela vermelha são visuais e vivem nos componentes; aqui ficam as
 * duas partes que não são pixel — som e vibração. Motivo de existirem: quem
 * está em cima de uma empilhadeira, de luva, com o galpão barulhento, confirma
 * o bipe pelo som antes de olhar a tela; e quem está com o coletor no bolso do
 * colete sente a vibração. Um sem o outro deixa metade dos operadores sem
 * confirmação.
 *
 * Sem dependência nova: WebAudio cru (dois osciladores) e `navigator.vibrate`.
 * Tudo é BEST-EFFORT — navegador sem áudio, sem permissão de autoplay ou sem
 * motor de vibração não pode derrubar um movimento de armazém.
 */

// Um AudioContext por página (o navegador limita quantos existem). Criado
// preguiçosamente no PRIMEIRO bipe: criado no import, nasce "suspended" pela
// política de autoplay e nunca toca.
let audioContext = null;

function getAudioContext() {
    if (typeof window === 'undefined') {
        return null;
    }
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) {
        return null;
    }
    if (!audioContext) {
        try {
            audioContext = new AudioContextCtor();
        } catch {
            return null;
        }
    }
    if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
    }
    return audioContext;
}

/** Um tom simples. `startAt` é o deslocamento em segundos a partir de agora. */
function tone(frequency, durationMs, { startAt = 0, gain = 0.09, type = 'sine' } = {}) {
    const context = getAudioContext();
    if (!context) {
        return;
    }
    try {
        const oscillator = context.createOscillator();
        const amplifier = context.createGain();
        const startTime = context.currentTime + startAt;
        const endTime = startTime + durationMs / 1000;

        oscillator.type = type;
        oscillator.frequency.setValueAtTime(frequency, startTime);
        // Rampa curta nas pontas: corta o "clique" de ligar/desligar o oscilador.
        amplifier.gain.setValueAtTime(0.0001, startTime);
        amplifier.gain.exponentialRampToValueAtTime(gain, startTime + 0.01);
        amplifier.gain.exponentialRampToValueAtTime(0.0001, endTime);

        oscillator.connect(amplifier);
        amplifier.connect(context.destination);
        oscillator.start(startTime);
        oscillator.stop(endTime + 0.02);
    } catch {
        // Áudio é conforto, não requisito.
    }
}

function vibrate(pattern) {
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate(pattern);
        }
    } catch {
        // idem
    }
}

/** Bipe de SUCESSO: dois tons SUBINDO (660 Hz → 990 Hz) + vibração curta. */
export function PlayScanSuccess() {
    tone(660, 90);
    tone(990, 120, { startAt: 0.1 });
    vibrate([30, 40, 30]);
}

/**
 * Bipe de FALHA: um tom GRAVE e longo (220 Hz, onda quadrada) + vibração
 * longa. Grave × agudo é a diferença que se ouve sem prestar atenção — é o
 * ponto do par.
 */
export function PlayScanError() {
    tone(220, 420, { gain: 0.12, type: 'square' });
    vibrate([120, 70, 120]);
}

/** Confirmação do movimento gravado — acorde curto, distinto do bipe de passo. */
export function PlayMovementDone() {
    tone(660, 110);
    tone(880, 110, { startAt: 0.11 });
    tone(1320, 180, { startAt: 0.22 });
    vibrate([40, 50, 40, 50, 90]);
}
